import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, X, Calendar, ChevronLeft, Loader2,
  CheckCircle, AlertTriangle, Info, Trash2, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const STATUS_CONFIG = {
  approved:               { label: 'Aprobadas',    color: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-300' },
  pending:                { label: 'Pendiente',    color: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-300' },
  rejected:               { label: 'Rechazada',    color: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-300' },
  cancelled:              { label: 'Cancelada',    color: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800/40 dark:text-slate-400' },
  cancellation_requested: { label: 'Canc. pedida', color: 'bg-orange-100 text-orange-800 border-orange-300' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const CUTOFF_MONTH = 4; // April
const CUTOFF_DAY   = 30;

function isCutoffPassed(year) {
  const today = new Date();
  return today > new Date(year, CUTOFF_MONTH - 1, CUTOFF_DAY);
}

// ── Balance card ─────────────────────────────────────────────────────────────
function BalanceCard({ label, total, used, pending, color, warning }) {
  const avail = Math.max(0, total - used - pending);
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-3">{label}</p>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          { v: total,   l: 'Total' },
          { v: used,    l: 'Usados' },
          { v: pending, l: 'Pendientes' },
          { v: avail,   l: 'Disponibles', bold: true },
        ].map(({ v, l, bold }) => (
          <div key={l}>
            <div className={`text-xl font-bold ${bold ? 'text-brand' : ''}`}>{(v ?? 0).toFixed(0)}</div>
            <div className="text-xs opacity-60">{l}</div>
          </div>
        ))}
      </div>
      {warning && (
        <p className="mt-2 text-xs flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" />{warning}
        </p>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function VacationRecordsAdmin() {
  const { user }                  = useAuth();
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [employee, setEmployee]   = useState(null);
  const [balances, setBalances]   = useState({});
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm]           = useState({ start_date: '', end_date: '', notes: '' });
  const [saving, setSaving]       = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  const currentYear = new Date().getFullYear();

  // ── Search employees ──────────────────────────────────────────────────────
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/vacation-balance/search', { params: { q: query } });
        setResults(res.data);
      } catch (err) { console.warn(err); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // ── Load employee data ────────────────────────────────────────────────────
  const loadEmployee = useCallback(async (userId) => {
    setLoading(true);
    try {
      const res = await api.get(`/vacations/admin/employee/${userId}`);
      setEmployee(res.data.employee);
      setBalances(res.data.balances || {});
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Error cargando los datos del empleado');
    } finally {
      setLoading(false);
    }
  }, []);

  const selectEmployee = (emp) => {
    setQuery('');
    setResults([]);
    loadEmployee(emp.id);
  };

  const clearEmployee = () => {
    setEmployee(null);
    setBalances({});
    setRequests([]);
    setQuery('');
  };

  // ── Record period ─────────────────────────────────────────────────────────
  const handleRecord = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error('Selecciona fecha de inicio y fin');
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error('La fecha fin debe ser posterior a la de inicio');
      return;
    }
    setSaving(true);
    try {
      await api.post('/vacations/admin/record', {
        user_id: employee.id,
        start_date: form.start_date,
        end_date: form.end_date,
        notes: form.notes || ''
      });
      toast.success('Periodo grabado correctamente');
      setShowDialog(false);
      setForm({ start_date: '', end_date: '', notes: '' });
      loadEmployee(employee.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al grabar el periodo');
    } finally {
      setSaving(false);
    }
  };

  // ── Cancel vacation ───────────────────────────────────────────────────────
  const handleCancel = async (reqId) => {
    if (!window.confirm('¿Seguro que quieres cancelar este periodo? Se restaurará el saldo.')) return;
    setCancellingId(reqId);
    try {
      await api.delete(`/vacations/admin/record/${reqId}`);
      toast.success('Periodo cancelado y saldo restaurado');
      loadEmployee(employee.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar el periodo');
    } finally {
      setCancellingId(null);
    }
  };

  // ── Carryover warning ──────────────────────────────────────────────────────
  const prevYear = currentYear - 1;
  const prevBal  = balances[prevYear];
  const carryoverAvail = prevBal
    ? Math.max(0, (prevBal.carryover_days || 0) - (prevBal.carryover_used || 0) - (prevBal.carryover_pending || 0))
    : 0;
  const cutoffPassed = isCutoffPassed(currentYear);

  // ── Dialog date change carryover alert ────────────────────────────────────
  const dialogEndIsAfterCutoff = form.end_date && new Date(form.end_date) > new Date(currentYear, CUTOFF_MONTH - 1, CUTOFF_DAY);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-brand/10">
          <Calendar className="h-5 w-5 text-brand" />
        </div>
        <div>
          <h1 className="text-xl font-bold font-heading">Gestión de Vacaciones</h1>
          <p className="text-sm text-muted-foreground">Graba y gestiona periodos de vacaciones directamente para cualquier empleado</p>
        </div>
      </div>

      {/* Search box */}
      {!employee && (
        <div className="relative max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="vacation-admin-search"
            className="pl-9"
            placeholder="Buscar empleado por nombre o email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
          <AnimatePresence>
            {results.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                className="absolute z-50 top-full mt-1 w-full bg-card border border-border rounded-xl shadow-lg overflow-hidden"
              >
                {results.map((emp) => (
                  <button
                    key={emp.id}
                    data-testid={`emp-result-${emp.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left"
                    onClick={() => selectEmployee(emp)}
                  >
                    <div className="h-8 w-8 rounded-full bg-brand/10 flex items-center justify-center text-brand font-bold text-sm flex-shrink-0">
                      {emp.first_name?.[0]}{emp.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{emp.first_name} {emp.last_name}</p>
                      <p className="text-xs text-muted-foreground">{emp.email}</p>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Employee panel */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-brand" />
        </div>
      )}

      {employee && !loading && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
          {/* Employee card */}
          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-brand/10 flex items-center justify-center text-brand font-bold text-lg flex-shrink-0">
              {employee.first_name?.[0]}{employee.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-base">{employee.first_name} {employee.last_name}</p>
              <p className="text-sm text-muted-foreground">{employee.email}</p>
              {employee.puesto && <p className="text-xs text-muted-foreground mt-0.5">{employee.puesto}{employee.manager_name ? ` · Manager: ${employee.manager_name}` : ''}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={clearEmployee} data-testid="btn-clear-employee">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Balance cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {balances[currentYear] && (
              <BalanceCard
                label={`Vacaciones ${currentYear}`}
                total={balances[currentYear].vacation_total}
                used={balances[currentYear].vacation_used}
                pending={balances[currentYear].vacation_pending}
                color="bg-card border-border"
              />
            )}
            {prevBal && (prevBal.carryover_days || 0) > 0 && (
              <BalanceCard
                label={`Arrastre ${prevYear} → ${currentYear}`}
                total={prevBal.carryover_days}
                used={prevBal.carryover_used}
                pending={prevBal.carryover_pending}
                color={cutoffPassed
                  ? 'bg-slate-50 border-slate-200 dark:bg-slate-800/30 dark:border-slate-700'
                  : 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800'}
                warning={cutoffPassed
                  ? `Plazo de arrastre caducado el 30/04/${currentYear}`
                  : `Días disponibles hasta el 30/04/${currentYear}`}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-base">Periodos de vacaciones</h2>
            <Button
              data-testid="btn-add-vacation-period"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setForm({ start_date: today, end_date: today, notes: '' });
                setShowDialog(true);
              }}
              className="bg-brand hover:bg-brand/90 text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Grabar periodo
            </Button>
          </div>

          {/* Requests table */}
          {requests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground bg-muted/20 rounded-xl border border-border">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay periodos registrados para este empleado</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    {['Inicio', 'Fin', 'Días lab.', 'Arrastre', 'Año', 'Estado', 'Acciones'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {requests.map((r) => {
                    const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                    const canCancel = r.status !== 'cancelled' && r.status !== 'rejected';
                    return (
                      <tr key={r.id} data-testid={`vacation-row-${r.id}`}
                        className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium">{fmt(r.start_date)}</td>
                        <td className="px-4 py-3">{fmt(r.end_date)}</td>
                        <td className="px-4 py-3 text-center font-semibold">{r.working_days}</td>
                        <td className="px-4 py-3 text-center">
                          {r.carryover_days_used > 0
                            ? <span className="text-amber-600 font-medium">{r.carryover_days_used} d.</span>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3">{r.start_date?.split('-')[0]}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          {r.created_by_admin && (
                            <span className="ml-1 text-xs text-muted-foreground">(admin)</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {canCancel && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`btn-cancel-vacation-${r.id}`}
                              disabled={cancellingId === r.id}
                              onClick={() => handleCancel(r.id)}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              {cancellingId === r.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      )}

      {/* Record period dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="dialog-record-vacation">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-brand" />
              Grabar periodo de vacaciones
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="bg-muted/40 rounded-lg p-3 flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium">{employee?.first_name} {employee?.last_name}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Fecha inicio *</label>
                <Input
                  data-testid="input-start-date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Fecha fin *</label>
                <Input
                  data-testid="input-end-date"
                  type="date"
                  value={form.end_date}
                  min={form.start_date}
                  onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Notas (opcional)</label>
              <Input
                data-testid="input-notes"
                placeholder="Observaciones…"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>

            {/* Carryover warning */}
            {carryoverAvail > 0 && !cutoffPassed && dialogEndIsAfterCutoff && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex gap-2 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Este periodo se extiende más allá del <strong>30 de abril de {currentYear}</strong>.
                  Los días de arrastre ({carryoverAvail} días de {prevYear}) solo son válidos hasta esa fecha.
                  El sistema bloqueará si algún día cae en arrastre.
                </span>
              </div>
            )}

            {cutoffPassed && carryoverAvail > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 flex gap-2 text-xs text-amber-700 dark:text-amber-300">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>El plazo de arrastre de {prevYear} ya caducó (30/04/{currentYear}). Los {carryoverAvail} días pendientes no pueden usarse.</span>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button
                data-testid="btn-confirm-record"
                className="flex-1 bg-brand hover:bg-brand/90 text-white"
                disabled={saving || !form.start_date || !form.end_date}
                onClick={handleRecord}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Grabar aprobado
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
